import { useCompanies } from "@/hooks/useCompanies";
import {
  useCompanyConnection,
  useStartFreeAgentConnect,
  useSyncFreeAgent,
} from "@/hooks/useFreeAgent";
import { Link } from "react-router-dom";

/**
 * Integrations live here, not buried under My company. v1 only has FreeAgent
 * + Xero (Xero coming next), keyed off the user's primary company.
 *
 * Future home for: bank feeds, Stripe, Shopify (goecom), HMRC MTD, Companies
 * House push-sync, etc.
 */
export default function Integrations() {
  const companies = useCompanies();
  const primary = companies.data?.[0];

  if (companies.isLoading) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  if (!primary) {
    return (
      <section className="space-y-4">
        <h1 className="text-3xl font-semibold">Integrations</h1>
        <div className="rounded-lg border p-8 text-center text-muted-foreground">
          Add a company before connecting an integration.{" "}
          <Link
            to="/dashboard/app/companies/new"
            className="text-foreground underline"
          >
            Add a company →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold">Integrations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your accounting and other tools so your dashboard reflects real
          numbers, not estimates. Tokens are encrypted and only used to pull data
          for your dashboard.
        </p>
      </header>

      <IntegrationCard
        title="FreeAgent"
        description="Pulls 3 years of trial balance + balance sheet. Refreshes on demand."
        companyId={primary.id}
      />

      <ComingSoon
        title="Xero"
        description="Same as FreeAgent — connect, sync, see your numbers. Wiring up next."
      />
      <ComingSoon
        title="Bank feeds (Open Banking)"
        description="Live cash position from your business bank. Backed by an FCA-regulated AISP."
      />
      <ComingSoon
        title="HMRC (Making Tax Digital)"
        description="VAT submissions + tax estimates directly from HMRC."
      />
    </section>
  );
}

function IntegrationCard({
  title,
  description,
  companyId,
}: {
  title: string;
  description: string;
  companyId: number;
}) {
  const connection = useCompanyConnection(companyId);
  const startConnect = useStartFreeAgentConnect();
  const sync = useSyncFreeAgent(companyId);

  return (
    <section className="rounded-lg border p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        {connection.data ? (
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            Connected
          </span>
        ) : null}
      </div>

      {connection.isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : connection.data ? (
        <div className="mt-4 space-y-2 text-sm">
          <div>
            Status: <span className="font-medium">{connection.data.status}</span>
          </div>
          {connection.data.last_synced_at ? (
            <div>
              Last sync:{" "}
              <span className="text-muted-foreground">
                {new Date(connection.data.last_synced_at).toLocaleString()}
              </span>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              className="rounded-md border px-3 py-1.5 hover:bg-secondary disabled:opacity-50"
            >
              {sync.isPending ? "Syncing…" : "Sync now"}
            </button>
          </div>
          {sync.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {(sync.error as Error)?.message ?? "Sync failed."}
            </div>
          ) : null}
          {sync.isSuccess ? (
            <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
              Sync complete.
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-4">
          <button
            type="button"
            onClick={async () => {
              const redirectUri = `${window.location.origin}/auth/freeagent/callback`;
              const res = await startConnect.mutateAsync({
                companyId,
                redirectUri,
              });
              window.location.href = res.authorize_url;
            }}
            disabled={startConnect.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Connect {title}
          </button>
        </div>
      )}
    </section>
  );
}

function ComingSoon({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <section className="rounded-lg border border-dashed p-6 opacity-70">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <span className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground">
          Coming soon
        </span>
      </div>
    </section>
  );
}
